import React from 'react';
import { numberToCurrencyString } from 'further-ui/utils';
import { EqualisationFeesFormValues } from './FormWrapper';
import useEqualiseFees from '../../hooks/useEqualiseFees';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import NumberInput from 'further-ui/components/NumberInput';

interface FeeCardRowProps {
  index: number;
  value: EqualisationFeesFormValues['totalCommitmentFees'][number];
  baseCurrency: string;
  onChange: (
    value: EqualisationFeesFormValues['totalCommitmentFees'][number],
  ) => void;
}

const FeeCardRow: React.FC<FeeCardRowProps> = ({
  index,
  value,
  baseCurrency,
  onChange,
}) => {
  const { updateSingleFeeField } = useEqualiseFees();

  const rows = (value?.lpFees ?? []).map((fee, feeIndex) => ({
    ...fee,
    id: fee.id || `${index}-${feeIndex}`,
    baseCurrency,
    rowIndex: feeIndex,
  }));

  const columns: Array<TableColumn<(typeof rows)[number]>> = [
    {
      key: 'accrualDate',
      label: 'Accrual date',
      render: (row) => row.accrualDate.toReadable(),
    },
    {
      key: 'feeTemplateName',
      label: 'Fee template name',
      render: (row) => row.feeTemplateName,
    },
    {
      key: 'totalCommitmentLcy',
      label: 'Total commitment',
      render: (row) =>
        numberToCurrencyString(row.totalCommitmentLcy, {
          fallback: '-',
          currency: row.baseCurrency,
        }),
    },
    {
      key: 'equalisationFeeExTaxLcy',
      label: 'Fee (ex tax)',
      render: (row) => (
        <NumberInput
          value={row.equalisationFeeExTaxLcy}
          currencyStartAdornment
          currency={row.baseCurrency}
          decimals={2}
          fixedDecimalScale
          onChange={(newFeeValue) => {
            onChange(
              updateSingleFeeField(
                value,
                'equalisationFeeExTaxLcy',
                newFeeValue ?? 0,
                row.rowIndex,
              ),
            );
          }}
        />
      ),
    },
    {
      key: 'equalisationSalesTaxAmountLcy',
      label: 'Sales tax',
      render: (row) => (
        <NumberInput
          value={row.equalisationSalesTaxAmountLcy}
          currencyStartAdornment
          decimals={2}
          fixedDecimalScale
          currency={row.baseCurrency}
          onChange={(newFeeValue) => {
            onChange(
              updateSingleFeeField(
                value,
                'equalisationSalesTaxAmountLcy',
                newFeeValue ?? 0,
                row.rowIndex,
              ),
            );
          }}
        />
      ),
    },
    {
      key: 'equalisationFeeIncTaxLcy',
      label: 'Fee (inc tax)',
      render: (row) =>
        numberToCurrencyString(row.equalisationFeeIncTaxLcy, {
          fallback: '-',
          currency: row.baseCurrency,
        }),
    },
  ];

  return (
    <Table
      emptyMessage="No fees"
      borderless
      showTopBorder
      columns={columns}
      rows={rows}
    />
  );
};

export default FeeCardRow;
